import type { RouteTemplate } from "./RouteTemplate";
import { isRouteParameter, isSpaceIdPlaceholder } from "./RouteTemplate";
import type { Url } from "./Url";
import type { NewQueryParamValues, QueryParamValues, UnknownQueryParam } from "./query/QueryStringParam";
import { serializeQueryParams } from "./query/QueryStringParam";

interface PartialRouteDefinition<RouteParameters, QueryParameters extends UnknownQueryParam[]> {
    relativeTemplate: RouteTemplate<never>;
    absoluteTemplate: RouteTemplate<RouteParameters>;
    queryParameters: QueryParameters;
}

export interface RouteDefinition<RouteParameters, QueryParameters extends UnknownQueryParam[]> extends PartialRouteDefinition<RouteParameters, QueryParameters> {
    generateUrl: OptionalParamsIfEmpty<(routeParams: RouteParameters, queryParams?: NeverIfEmpty<NewQueryParamValues<QueryParamValues<QueryParameters>>>) => Url>;
    getDebugString: (options?: { excludeQueryParams?: boolean }) => string;
}

type NeverIfEmpty<T> = keyof T extends never ? never : T;

// A common case is a route with no parameters. It is more ergonomic to omit the params object in this scenario

type OptionalParamsIfEmpty<T> = T extends (routeParams: infer RP, queryParams?: infer QP) => Url ? ({} extends RP ? (queryParams?: QP) => Url : T) : T;

export function createRouteDefintion<RouteParameters, QueryParams extends UnknownQueryParam[]>({
    absoluteTemplate,
    relativeTemplate,
    queryParameters: queryParamDefinitions,
}: PartialRouteDefinition<RouteParameters, QueryParams>): RouteDefinition<RouteParameters, QueryParams> {
    const routeDefinition: RouteDefinition<RouteParameters, QueryParams> = {
        absoluteTemplate,
        relativeTemplate,
        generateUrl,
        queryParameters: queryParamDefinitions,
        getDebugString,
    };
    return routeDefinition;

    function generateUrl(routeParams: RouteParameters, queryParams?: NewQueryParamValues<QueryParamValues<QueryParams>>): Url;
    function generateUrl(queryParams?: NewQueryParamValues<QueryParamValues<QueryParams>>): Url;
    function generateUrl(routeParamsOrQueryParams?: RouteParameters | NewQueryParamValues<QueryParamValues<QueryParams>>, queryParams?: NewQueryParamValues<QueryParamValues<QueryParams>>): Url {
        if (isRouteParams(routeParamsOrQueryParams)) {
            return generateUrlInternal(routeParamsOrQueryParams, queryParams);
        } else {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return generateUrlInternal({} as RouteParameters, routeParamsOrQueryParams);
        }
    }

    function generateUrlInternal(routeParams: RouteParameters, queryParams?: NewQueryParamValues<QueryParamValues<QueryParams>>) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const emptyQueryParams = {} as QueryParamValues<QueryParams>;

        return {
            resolveWithSpaceId(currentSpaceId: string | null) {
                return `${getRoute()}${getQueryString()}`;

                function getRoute() {
                    return absoluteTemplate
                        .map((part) => {
                            if (typeof part === "string") return part;

                            if (isRouteParameter(part)) {
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                return encodeURIComponent(routeParams[part.name] as string);
                            }

                            if (currentSpaceId) {
                                return `/${currentSpaceId}`;
                            }
                            return "";
                        })
                        .join("");
                }

                function getQueryString() {
                    return generateQueryString(queryParamDefinitions, queryParams ?? emptyQueryParams);
                }
            },
        };
    }

    function isRouteParams(routeParamsOrQueryParams?: RouteParameters | NewQueryParamValues<QueryParamValues<QueryParams>>): routeParamsOrQueryParams is RouteParameters {
        return absoluteTemplate.some((exp) => typeof exp !== "string" && exp.type === "parameter");
    }

    function getDebugString(options?: { excludeQueryParams?: boolean }): string {
        const routeString = absoluteTemplate
            .map((part) => {
                if (typeof part === "string") {
                    return part;
                }
                if (isSpaceIdPlaceholder(part)) {
                    return "/{spaceId}";
                }
                return `{${part.name}}`;
            })
            .join("");

        if (options?.excludeQueryParams === true) {
            return routeString;
        }

        const queryStringParameters = queryParamDefinitions.map((p) => p.name);
        const queryStringSummaryString = queryStringParameters.length > 0 ? `?{${queryStringParameters.join(",")}}` : "";
        return `${routeString}${queryStringSummaryString}`;
    }
}

export function generateQueryString<QueryParams extends UnknownQueryParam[]>(queryParameter: [...QueryParams], queryValues: NewQueryParamValues<QueryParamValues<QueryParams>>): string {
    const serializedQueryParams = serializeQueryParams(queryParameter, queryValues);

    const queryParameterKeyValues = serializedQueryParams.flatMap(({ name, values }) => {
        return values.map((value) => [name, value]);
    });

    const urlSearchParams = new URLSearchParams(queryParameterKeyValues);
    // Sorting keeps things more stable and deterministic
    urlSearchParams.sort();
    const queryParameterString = urlSearchParams.toString();

    return queryParameterString.length > 0 ? `?${queryParameterString}` : "";
}
